import { useState, useEffect } from 'react';
import { updateReport, updateReportStatus, uploadSiteImage, getSiteImageUrl, deleteSiteImage, getScopeOfAssessmentTypes, getOrganisationMembers } from '../../api';

export default function DetailsTab({ report, onSave, readOnly = false }) {
  const [form, setForm] = useState({
    organisationId: report.organisationId,
    siteId: report.siteId,
    locationId: report.locationId,
    assessorUserId: report.assessorUserId,
    assessmentDate: report.assessmentDate || '',
    reviewDate: report.reviewDate || '',
    scopeOfAssessment: report.scopeOfAssessment || '',
    preparedFor: report.preparedFor || '',
    isCqcGoverned: report.isCqcGoverned || false,
    versionNumber: report.versionNumber || '',
    reviewerUserId: report.reviewerUserId || '',
    reviewNotes: report.reviewNotes || '',
  });
  const [msg, setMsg] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [scopeTypes, setScopeTypes] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);

  useEffect(() => {
    getScopeOfAssessmentTypes().then(setScopeTypes).catch(() => {});
    if (report.organisationId) {
      getOrganisationMembers(report.organisationId).then(setOrgUsers).catch(() => {});
    }
  }, [report.organisationId]);

  useEffect(() => {
    setForm({
      organisationId: report.organisationId,
      siteId: report.siteId,
      locationId: report.locationId,
      assessorUserId: report.assessorUserId,
      assessmentDate: report.assessmentDate || '',
      reviewDate: report.reviewDate || '',
      scopeOfAssessment: report.scopeOfAssessment || '',
      preparedFor: report.preparedFor || '',
      isCqcGoverned: report.isCqcGoverned || false,
      versionNumber: report.versionNumber || '',
      reviewerUserId: report.reviewerUserId || '',
      reviewNotes: report.reviewNotes || '',
    });
  }, [report]);

  useEffect(() => {
    if (report.siteImagePath) {
      const token = localStorage.getItem('token');
      fetch(getSiteImageUrl(report.id), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((res) => (res.ok ? res.blob() : null))
        .then((blob) => {
          if (blob) setImagePreview(URL.createObjectURL(blob));
        })
        .catch(() => {});
    } else {
      setImagePreview(null);
    }
  }, [report.siteImagePath, report.id]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async () => {
    if (form.reviewDate && form.assessmentDate && form.reviewDate < form.assessmentDate) {
      setMsg('Review date must be on or after assessment date');
      return;
    }
    try {
      const payload = { ...form, reviewerUserId: form.reviewerUserId ? Number(form.reviewerUserId) : null };
      await updateReport(report.id, payload);
      setMsg('Saved');
      onSave();
      setTimeout(() => setMsg(''), 2000);
    } catch (err) { setMsg(err.message); }
  };

  const handleStatus = async (status) => {
    try {
      // Save details first so reviewer/notes are persisted before status check
      const payload = { ...form, reviewerUserId: form.reviewerUserId ? Number(form.reviewerUserId) : null };
      await updateReport(report.id, payload);
      await updateReportStatus(report.id, status);
      setMsg(`Status updated to ${status.replace('_', ' ')}`);
      onSave();
      setTimeout(() => setMsg(''), 2000);
    } catch (err) {
      setMsg('Failed to update status: ' + err.message);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadSiteImage(report.id, file);
      onSave();
      setMsg('Image uploaded');
      setTimeout(() => setMsg(''), 2000);
    } catch (err) {
      setMsg(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleImageRemove = async () => {
    try {
      await deleteSiteImage(report.id);
      setImagePreview(null);
      onSave();
      setMsg('Image removed');
      setTimeout(() => setMsg(''), 2000);
    } catch (err) {
      setMsg(err.message);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', background: '#fafafa' }}>
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>Site Photograph</label>
        {imagePreview ? (
          <div>
            <img src={imagePreview} alt="Site" style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px', marginBottom: '0.5rem', display: 'block' }} />
            {!readOnly && <button className="btn btn-outline" style={{ color: '#c00', borderColor: '#c00' }} onClick={handleImageRemove}>Remove Image</button>}
          </div>
        ) : (
          !readOnly && <div>
            <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
            {uploading && <span style={{ marginLeft: '0.5rem', color: '#666' }}>Uploading...</span>}
          </div>
        )}
      </div>

      <div style={{ padding: '0.75rem', marginBottom: '1rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6 }}>
        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>{report.siteLocationName || 'No site linked'}</div>
        {report.siteLocationAddress && <div style={{ color: '#6b7280', fontSize: '0.85rem', whiteSpace: 'pre-line' }}>{report.siteLocationAddress}</div>}
      </div>
      <div className="form-group"><label>Prepared For</label><input name="preparedFor" value={form.preparedFor} onChange={handleChange} disabled={readOnly} /></div>
      <div className="form-group">
        <label>Scope of Assessment</label>
        <div className="checkbox-group">
          {scopeTypes.map((st) => (
            <label key={st.code}>
              <input
                type="radio"
                name="scopeOfAssessment"
                value={st.code}
                checked={form.scopeOfAssessment === st.code}
                onChange={handleChange}
                disabled={readOnly}
              />
              {st.label}
            </label>
          ))}
        </div>
      </div>
      <div className="form-group">
        <label>Is this site governed by the CQC (Care Quality Commission)?</label>
        <div className="checkbox-group">
          <label>
            <input
              type="radio"
              name="isCqcGoverned"
              value="true"
              checked={form.isCqcGoverned === true}
              onChange={() => setForm({ ...form, isCqcGoverned: true })}
              disabled={readOnly}
            />
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="isCqcGoverned"
              value="false"
              checked={form.isCqcGoverned === false}
              onChange={() => setForm({ ...form, isCqcGoverned: false })}
              disabled={readOnly}
            />
            No
          </label>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Version Number</label>
          <input name="versionNumber" value={form.versionNumber} onChange={handleChange} placeholder="e.g. v1.0" disabled={readOnly} />
        </div>
        <div className="form-group">
          <label>Report Revision Number</label>
          <input value={report.systemVersion || '\u2014'} disabled style={{ background: '#f5f5f5' }} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group"><label>Assessment Date</label><input type="date" name="assessmentDate" value={form.assessmentDate} onChange={handleChange} disabled={readOnly} /></div>
        <div className="form-group"><label>Review Date</label><input type="date" name="reviewDate" value={form.reviewDate} onChange={handleChange} disabled={readOnly} /></div>
      </div>
      {!readOnly && report.status !== 'ASSESSOR_COMPLETED' && report.status !== 'QA_APPROVED' && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={handleSave}>Save Details</button>
          <button className="btn btn-outline" style={{ color: '#333', borderColor: '#ccc' }} onClick={() => handleStatus('IN_PROGRESS')}>Mark In Progress</button>
          <button className="btn btn-outline" style={{ color: '#10b981', borderColor: '#10b981' }} onClick={() => handleStatus('ASSESSOR_COMPLETED')}>Assessor Complete</button>
        </div>
      )}

      {(report.status === 'ASSESSOR_COMPLETED' || report.status === 'QA_APPROVED') && (
        <div style={{ marginTop: '2rem', padding: '1.5rem', border: '2px solid #7c3aed', borderRadius: 10, background: '#faf5ff' }}>
          <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem', color: '#7c3aed' }}>QA Review</h3>
          <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#6b7280' }}>
            {report.status === 'QA_APPROVED'
              ? 'This report has been QA approved.'
              : 'The assessor has completed this report. Fill in the review details below and approve.'}
          </p>
          <div className="form-group">
            <label>Reviewed By</label>
            <select name="reviewerUserId" value={form.reviewerUserId} onChange={handleChange} disabled={readOnly || report.status === 'QA_APPROVED'}>
              <option value="">— Select reviewer —</option>
              {orgUsers.map((u) => (
                <option key={u.userId} value={u.userId}>{u.firstName} {u.lastName}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Review Notes</label>
            <textarea
              name="reviewNotes"
              value={form.reviewNotes}
              onChange={handleChange}
              disabled={readOnly || report.status === 'QA_APPROVED'}
              rows={5}
              placeholder="Internal QA notes — not included in exported report"
              style={{ resize: 'vertical', fontSize: '0.95rem' }}
            />
          </div>
          {!readOnly && report.status === 'ASSESSOR_COMPLETED' && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={handleSave}>Save Details</button>
              <button
                className="btn"
                style={{ background: '#7c3aed', color: '#fff', fontSize: '1rem', padding: '0.6rem 1.5rem' }}
                onClick={() => handleStatus('QA_APPROVED')}
                disabled={!form.reviewerUserId || !form.reviewNotes}
                title={!form.reviewerUserId || !form.reviewNotes ? 'Reviewer and review notes are required' : ''}
              >QA Approve</button>
              <button className="btn btn-outline" style={{ color: '#333', borderColor: '#ccc' }} onClick={() => handleStatus('IN_PROGRESS')}>Reopen as In Progress</button>
            </div>
          )}
          {!readOnly && report.status === 'QA_APPROVED' && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-outline" style={{ color: '#333', borderColor: '#ccc' }} onClick={() => handleStatus('ASSESSOR_COMPLETED')}>Revoke QA Approval</button>
            </div>
          )}
        </div>
      )}

      {report.status !== 'ASSESSOR_COMPLETED' && report.status !== 'QA_APPROVED' && (
        <div style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #e0e7ef', borderRadius: '8px', background: '#f8fafc' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.75rem', color: '#475569' }}>QA Review (internal only)</label>
          <p style={{ margin: '0', fontSize: '0.85rem', color: '#9ca3af' }}>QA review is available once the assessor has marked the report as complete.</p>
        </div>
      )}
      {msg && <div className="alert alert-success" style={{ marginTop: '0.5rem' }}>{msg}</div>}
    </div>
  );
}
